import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Tags, Upload, Download, Search, Ban, History } from "lucide-react";
import { api, idr, pesanGalat, fmtNum } from "../../../lib/api";
import { formatTanggal } from "../../../lib/tanggal";
import { useDebounce } from "../../../hooks/useDebounce";
import {
  MDR_BASE, Modal, DialogAlasan, unduhDenganToast, kelasInput, kelasTombol, kelasTombolUtama, kelasTombolKecil,
} from "./shared";

/** Master SRP Promo (brief C.4/C.5/C.15, keputusan C10) — satu master untuk
 *  semua tab. Append-only: periode yang tumpang tindih dengan periode AKTIF
 *  untuk Tipe + Storage yang sama DITOLAK per baris oleh server, jadi impor
 *  periode baru tidak pernah mengubah periode lama. Koreksi salah ketik =
 *  nonaktifkan periode (beralasan, tercatat) lalu impor ulang; periode yang
 *  sudah dipakai data Siap/Sudah Claim tidak bisa dinonaktifkan.
 *
 *  Daftar periode disaring DI SERVER (q/tanggal/aktif/impor) — daftar dipotong
 *  5000 baris, dan menyaring di layar membuat baris ke-5001 tidak pernah
 *  ketemu tapi layar melapor "tidak ada" dengan yakin (CLAUDE.md). */
export default function DialogSrp({ hak, onTutup, onSelesai }) {
  const [tab, setTab] = useState("periode");
  const [berkas, setBerkas] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hasil, setHasil] = useState(null);

  const [q, setQ] = useState("");
  const qDeb = useDebounce(q, 400);
  const [tanggal, setTanggal] = useState("");
  const [aktifF, setAktifF] = useState("true");
  const [imporF, setImporF] = useState(null);
  const [periode, setPeriode] = useState([]);
  const [terpotong, setTerpotong] = useState(false);
  const [memuat, setMemuat] = useState(true);
  const [riwayat, setRiwayat] = useState([]);
  const [nonaktif, setNonaktif] = useState(null);
  const [muatUlang, setMuatUlang] = useState(0);
  const [kunciBerkas, setKunciBerkas] = useState(0);

  const muatPeriode = useCallback(async () => {
    setMemuat(true);
    try {
      const params = {};
      if (qDeb.trim()) params.q = qDeb.trim();
      if (tanggal) params.tanggal = tanggal;
      if (aktifF) params.aktif = aktifF;
      if (imporF) params.impor_id = imporF.id;
      const r = await api.get(`${MDR_BASE}/srp`, { params });
      setPeriode(r.data.items || []);
      setTerpotong(!!r.data.terpotong);
    } catch (e) { toast.error(pesanGalat(e, "Gagal memuat Master SRP")); }
    finally { setMemuat(false); }
  }, [qDeb, tanggal, aktifF, imporF]);
  useEffect(() => { muatPeriode(); }, [muatPeriode, muatUlang]);

  useEffect(() => {
    api.get(`${MDR_BASE}/srp/impor`).then((r) => setRiwayat(r.data.items || []))
      .catch((e) => toast.error(pesanGalat(e, "Gagal memuat riwayat impor SRP")));
  }, [muatUlang]);

  const impor = async () => {
    if (!berkas) return;
    setBusy(true); setHasil(null);
    try {
      const fd = new FormData();
      fd.append("file", berkas);
      const r = await api.post(`${MDR_BASE}/srp/impor`, fd, { timeout: 180000 });
      setHasil(r.data);
      toast.success(r.data.message || "Impor selesai");
      setBerkas(null);
      setKunciBerkas((n) => n + 1);
      setMuatUlang((n) => n + 1);
      onSelesai?.();
    } catch (e) { toast.error(pesanGalat(e, "Impor Master SRP gagal")); }
    finally { setBusy(false); }
  };

  const kirimNonaktif = async (alasan) => {
    try {
      const r = await api.post(`${MDR_BASE}/srp/${nonaktif.id}/nonaktifkan`, { alasan });
      toast.success(r.data.message || "Periode dinonaktifkan");
      setNonaktif(null);
      setMuatUlang((n) => n + 1);
      onSelesai?.();
    } catch (e) { toast.error(pesanGalat(e, "Gagal menonaktifkan periode")); }
  };

  const tabKls = (k) => `h-9 px-3 rounded-lg text-sm font-medium ${tab === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`;

  return (
    <Modal judul="Master SRP Promo" ikon={Tags} onTutup={onTutup} lebar="max-w-4xl" testid="mdr-srp"
           sub="SRP per Tipe + Storage (tanpa warna) per periode berlaku — dipakai bersama semua tab.">
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" className={tabKls("periode")} onClick={() => setTab("periode")} data-testid="mdr-srp-tab-periode">Periode SRP</button>
          <button type="button" className={tabKls("riwayat")} onClick={() => setTab("riwayat")} data-testid="mdr-srp-tab-riwayat">
            <span className="inline-flex items-center gap-1"><History className="w-3.5 h-3.5" /> Riwayat Impor</span>
          </button>
          <div className="flex-1" />
          <button type="button" className={kelasTombol} data-testid="mdr-srp-template"
                  onClick={() => unduhDenganToast(`${MDR_BASE}/srp/template`, "template-master-srp.xlsx")}>
            <Download className="w-4 h-4" /> Unduh Template
          </button>
        </div>

        {hak.impor && (
          <div className="flex flex-wrap items-center gap-2 border border-dashed border-slate-300 rounded-lg p-3">
            <span className="text-xs font-medium text-slate-600">Import Master SRP Promo:</span>
            <input key={kunciBerkas} type="file" accept=".xlsx" data-testid="mdr-srp-berkas" onChange={(e) => setBerkas(e.target.files?.[0] || null)}
                   className="text-sm file:mr-2 file:h-9 file:px-3 file:rounded-lg file:border file:border-slate-200 file:bg-white file:text-sm" />
            <button type="button" onClick={impor} disabled={!berkas || busy} className={kelasTombolUtama} data-testid="mdr-srp-kirim">
              <Upload className="w-4 h-4" /> {busy ? "Mengimpor..." : "Impor"}
            </button>
          </div>
        )}
        {hasil && (
          <div className="border border-slate-200 rounded-lg p-3 text-sm space-y-1" data-testid="mdr-srp-hasil">
            <p><span className="font-mono font-semibold">{hasil.kode}</span>: <span className="text-emerald-700">{fmtNum(hasil.diterima)} diterima</span>,{" "}
              <span className={hasil.ditolak ? "text-rose-700" : "text-slate-500"}>{fmtNum(hasil.ditolak)} ditolak</span></p>
            {(hasil.gagal || []).length > 0 && (
              <ul className="text-xs max-h-40 overflow-y-auto space-y-0.5">
                {hasil.gagal.map((g, i) => <li key={i}><span className="font-mono">Baris {g.baris}</span>: {g.alasan}</li>)}
              </ul>
            )}
          </div>
        )}

        {tab === "periode" ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[180px]">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari tipe..." data-testid="mdr-srp-cari"
                       className={`${kelasInput} w-full pl-9`} />
              </div>
              <label className="text-xs text-slate-600 inline-flex items-center gap-1">Berlaku tgl
                <input type="date" value={tanggal} onChange={(e) => setTanggal(e.target.value)} data-testid="mdr-srp-tanggal" className={kelasInput} />
              </label>
              <select value={aktifF} onChange={(e) => setAktifF(e.target.value)} className={kelasInput} data-testid="mdr-srp-aktif">
                <option value="true">Aktif</option>
                <option value="false">Nonaktif</option>
                <option value="">Semua</option>
              </select>
              {imporF && (
                <button type="button" className={kelasTombolKecil} onClick={() => setImporF(null)} data-testid="mdr-srp-hapus-filter-impor">
                  Impor {imporF.kode} ✕
                </button>
              )}
            </div>
            <div className="border border-slate-200 rounded-lg overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                  <tr>
                    <th className="text-left px-2 py-2">Tipe + Storage</th>
                    <th className="text-left px-2 py-2">Periode Berlaku</th>
                    <th className="text-right px-2 py-2">SRP</th>
                    <th className="text-left px-2 py-2">Impor</th>
                    <th className="text-left px-2 py-2">Status</th>
                    <th className="px-2 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {memuat ? (
                    <tr><td colSpan={6} className="px-2 py-4 text-center text-slate-400">Memuat...</td></tr>
                  ) : periode.length === 0 ? (
                    <tr><td colSpan={6} className="px-2 py-4 text-center text-slate-400">Tidak ada periode SRP untuk filter ini</td></tr>
                  ) : periode.map((p) => (
                    <tr key={p.id} data-testid={`mdr-srp-row-${p.id}`}>
                      <td className="px-2 py-1.5 font-medium">{p.label_tipe}</td>
                      <td className="px-2 py-1.5">{formatTanggal(p.dari)} s.d. {formatTanggal(p.sampai)}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{idr(p.srp)}</td>
                      <td className="px-2 py-1.5 font-mono">{p.impor_kode || "-"}</td>
                      <td className="px-2 py-1.5">
                        {p.aktif ? <span className="text-emerald-700">Aktif</span> : (
                          <span className="text-slate-500" title={p.dinonaktifkan?.alasan}>
                            Nonaktif{p.dinonaktifkan?.alasan ? ` — ${p.dinonaktifkan.alasan}` : ""}
                          </span>
                        )}
                        {p.dipakai_klaim && <span className="ml-1 text-sky-700">· dipakai klaim</span>}
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        {hak.ubah && p.aktif && !p.dipakai_klaim && (
                          <button type="button" onClick={() => setNonaktif(p)} data-testid={`mdr-srp-nonaktif-${p.id}`}
                                  className="h-8 px-2.5 rounded-lg border border-rose-200 text-rose-700 hover:bg-rose-50 text-xs font-medium inline-flex items-center gap-1">
                            <Ban className="w-3.5 h-3.5" /> Nonaktifkan
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {terpotong && <p className="text-xs text-amber-700">Daftar dipotong 5.000 baris — persempit pencarian.</p>}
          </>
        ) : (
          <div className="border border-slate-200 rounded-lg overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                <tr>
                  <th className="text-left px-2 py-2">Kode</th>
                  <th className="text-left px-2 py-2">Berkas</th>
                  <th className="text-left px-2 py-2">Tanggal / Oleh</th>
                  <th className="text-right px-2 py-2">Diterima</th>
                  <th className="text-right px-2 py-2">Ditolak</th>
                  <th className="px-2 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {riwayat.length === 0 ? (
                  <tr><td colSpan={6} className="px-2 py-4 text-center text-slate-400">Belum ada impor Master SRP</td></tr>
                ) : riwayat.map((r) => (
                  <tr key={r.id} data-testid={`mdr-srp-impor-${r.id}`}>
                    <td className="px-2 py-1.5 font-mono">{r.kode}</td>
                    <td className="px-2 py-1.5 max-w-[180px] truncate" title={r.nama_file}>{r.nama_file || "-"}</td>
                    <td className="px-2 py-1.5">{formatTanggal(r.created_at, { denganJam: true })}<br />
                      <span className="text-slate-400">{r.created_by_name || r.created_by}</span></td>
                    <td className="px-2 py-1.5 text-right font-mono">{fmtNum(r.diterima)}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{fmtNum(r.ditolak)}</td>
                    <td className="px-2 py-1.5 text-right">
                      <button type="button" className={kelasTombolKecil} data-testid={`mdr-srp-impor-lihat-${r.id}`}
                              onClick={() => { setImporF(r); setAktifF(""); setTab("periode"); }}>
                        Lihat periodenya
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {nonaktif && (
        <DialogAlasan judul={`Nonaktifkan SRP ${nonaktif.label_tipe}?`} bahaya labelYa="Nonaktifkan" testid="mdr-srp-nonaktif-dialog"
                      keterangan={`Periode ${formatTanggal(nonaktif.dari)} s.d. ${formatTanggal(nonaktif.sampai)} · ${idr(nonaktif.srp)}. Setelah dinonaktifkan, periode yang sama boleh diimpor ulang dengan angka yang benar.`}
                      onYa={kirimNonaktif} onTutup={() => setNonaktif(null)} />
      )}
    </Modal>
  );
}
